import { AlertTriangle } from 'lucide-react';
import { contentCapacityCopy } from '@/features/product/text-size';
import { cn } from '@/lib/utils';

/**
 * Shows how much of a product's supporting-document quota is used, and — when a
 * selection was refused — an inline notice that STAYS until the next successful add.
 *
 * A toast alone was not enough: it disappears, so a user who selected 55 files and
 * got 8 was left with a list that silently stopped growing. The bar answers "how
 * close am I", the notice answers "what just happened to my files", and neither
 * depends on the user catching a transient message.
 *
 * Shared by both places files are attached — the create-product screen (staging into
 * the browser, nothing stored yet) and an existing product's uploader — so the two
 * read identically.
 */

export interface QuotaRejection {
  /** Names of the files that were not taken, in selection order. */
  names: string[];
  /** What stopped them: a ceiling, or a name already in the list. */
  reason: 'count' | 'size' | 'duplicate';
  /** The file the limit was reached at, for a concrete message. */
  stoppedAt: string | null;
}

function mb(bytes: number): number {
  return Math.floor(bytes / (1024 * 1024));
}


export function SupportingDocsQuota({
  usedFiles,
  maxFiles,
  usedBytes,
  maxBytes,
  rejection,
  onDismissRejection,
  textChars,
  textBudget,
}: {
  usedFiles: number;
  maxFiles: number;
  usedBytes: number;
  maxBytes: number;
  rejection?: QuotaRejection | null;
  onDismissRejection?: () => void;
  /** Converted text across the stored documents and the agents' reading budget.
   *  Omitted on the create screen, where nothing is converted yet. */
  textChars?: number;
  textBudget?: number;
}) {
  const content = textChars != null && textBudget != null && textBudget > 0 ? contentCapacityCopy(textChars, textBudget) : null;
  const filePct = maxFiles > 0 ? (usedFiles / maxFiles) * 100 : 0;
  const bytePct = maxBytes > 0 ? (usedBytes / maxBytes) * 100 : 0;
  // Fill the bar by whichever ceiling is closer — that's the one that will stop you.
  const pct = Math.min(100, Math.round(Math.max(filePct, bytePct)));
  const filesLeft = Math.max(0, maxFiles - usedFiles);
  const bytesLeft = Math.max(0, maxBytes - usedBytes);
  const full = filesLeft === 0 || bytesLeft <= 0;
  const nearlyFull = !full && pct >= 80;

  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between gap-3 text-[12px]">
        <span className="text-ink font-medium">
          {usedFiles} of {maxFiles} document{maxFiles === 1 ? '' : 's'}
        </span>
        <span className="text-muted tabular-nums">
          {mb(usedBytes)} / {mb(maxBytes)} MB
        </span>
      </div>
      <div
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-line"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Supporting document quota used"
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width]',
            full
              ? 'bg-state-error-accent'
              : nearlyFull
                ? 'bg-state-warning-accent'
                : 'bg-electric',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {full && !rejection && (
        <p className="text-state-warning-text mt-1.5 m-0 text-[12px]">
          {/* A product can sit ABOVE a limit: the limits are settings, and lowering one
              never deletes anything already uploaded. Those two cases carry a number the
              header does not, so they spell it out. Sitting EXACTLY at a ceiling does not
              — "10 of 10 documents" is already on the line above and the bar is already
              red — so repeating it as "has reached its 10-document limit" adds a sentence
              and no information. Give the way out instead. */}
          {usedFiles > maxFiles
            ? `This product has ${usedFiles} documents and the limit is now ${maxFiles}. Remove ${usedFiles - maxFiles + 1} to add another.`
            : usedBytes > maxBytes
              ? `This product holds ${mb(usedBytes)} MB and the limit is now ${mb(maxBytes)} MB. Remove some to add more.`
              : filesLeft === 0
                ? 'Remove a document to add another.'
                : 'Remove a document to free space.'}
        </p>
      )}
      {nearlyFull && !rejection && (
        <p className="text-muted mt-1.5 m-0 text-[12px]">
          {/* State BOTH remaining ceilings. Which one binds depends on the size of
              whatever the user adds next, which we cannot know: with 7 KB files the
              count is the real limit, with 30 MB files the size is. Naming only one
              misleads in whichever direction it guessed wrong. */}
          {`Room for ${filesLeft} more document${filesLeft === 1 ? '' : 's'} or ${mb(bytesLeft)} MB — whichever runs out first.`}
        </p>
      )}

      {content && (
        // LAST, below the count/size messages: the bar and everything directly under it
        // are the storage quota (documents and MB), and this is a different measure
        // entirely — what the agents can read in one run. Sitting between the bar and its
        // own "reached its N-document limit" line made it read as a third storage number.
        // A run is refused above 100%, so the user sees it before pressing Generate.
        // Deliberately no units or mechanism on the page.
        <p
          className={cn(
            'mt-1.5 m-0 text-[12px] tabular-nums',
            content.over || content.nearlyFull ? 'text-state-warning-text font-medium' : 'text-muted',
          )}
          data-testid="text-budget"
        >
          {content.text}
        </p>
      )}

      {rejection && rejection.names.length > 0 && (
        <div
          // Announced when it appears: this notice exists precisely so a refusal is not
          // missed, which has to hold for someone using a screen reader too. "polite"
          // waits for a pause rather than interrupting.
          role="status"
          aria-live="polite"
          className="border-state-warning-border bg-state-warning-bg-soft mt-2 flex items-start gap-2 rounded-lg border px-3 py-2"
        >
          <AlertTriangle
            className="text-state-warning-accent mt-0.5 size-4 shrink-0"
            aria-hidden
          />
          <div className="min-w-0 text-[12px]">
            <p className="text-state-warning-text m-0 font-medium">
              {rejection.names.length} file{rejection.names.length === 1 ? '' : 's'} not
              added
              {rejection.reason === 'duplicate'
                ? ' — already in the list. Uploading the same name twice keeps only one copy.'
                : rejection.stoppedAt
                  ? rejection.reason === 'size'
                    ? ` — the ${mb(maxBytes)} MB total was reached at ${rejection.stoppedAt}.`
                    : ` — the ${maxFiles}-document limit was reached at ${rejection.stoppedAt}.`
                  : '.'}
            </p>
            <p className="text-muted m-0 mt-0.5 break-words">
              {rejection.names.slice(0, 3).join(', ')}
              {rejection.names.length > 3
                ? ` and ${rejection.names.length - 3} more`
                : ''}
            </p>
            {onDismissRejection && (
              <button
                type="button"
                onClick={onDismissRejection}
                className="text-muted hover:text-ink mt-1 underline underline-offset-2"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
